import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Container, Grid, Button, Paper } from "@mui/material";
import { InsertPhoto, Person } from "@mui/icons-material";
import CustomAppBar from "../../components/AppBar";
import {
  CustomLabel,
  CustomTitle,
  CustomValue,
} from "../../components/Custom/CustomTypography";
import CustomTextField from "../../components/Custom/CustomTextField";
import LoaderContainer from "../../components/LoaderContainer";
import { usePersonalInformation } from "../../hooks/usePersonalInformation";
import { AddressFields } from "../../components/Famer/AddressFields";
import KYCFields from "../../components/Famer/KYCFields";
import { personalInformationSchema } from "../../components/Custom/ValidationSchema";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../components/Alert";
import { filterEmptyValues } from "../../globals/utils";
import placeholder from "../../assets/svg/placeholder.svg";

const validationSchema = personalInformationSchema;

function PersonalInformation() {
  const [id, setId] = useState(null); // Storing id as a number

  useEffect(() => {
    const data = localStorage.getItem("auth");
    if (data) {
      try {
        const parsedData = JSON.parse(data); // Parse the JSON string
        setId(parsedData.user.app_user.id); // Extract user ID
      } catch (error) {
        console.error("Error parsing localStorage data:", error);
      }
    }
  }, []);

  const {
    profileData,
    postOffices,
    pincodeError,
    loading,
    farmerId,
    handleChange,
    handleClick,
    handleImageUpload,
    previews,
    fetchAddressDetails,
  } = usePersonalInformation({ id });

  const [errors, setErrors] = useState({});
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (pincodeError) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        pin_code: pincodeError,
      }));
      return;
    }
    try {
      const filteredData = filterEmptyValues(profileData);
      await validationSchema.validate(filteredData, {
        abortEarly: false,
      });
      handleClick(filteredData);
    } catch (error) {
      if (error.name === "ValidationError") {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      }
      showAlert("error", "Error saving profile");
    }
  };

  return (
    <>
      <Helmet>
        <title>Personal Information - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container>
          <CustomTitle>{t("Personal Information")}</CustomTitle>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} style={{ marginTop: "10px" }}>
              {/* Basic Information */}
              <Grid item xs={4} style={{ marginLeft: "5px" }}>
                <CustomLabel>{t("Unique Id :")}</CustomLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomValue>{farmerId}</CustomValue>
              </Grid>

              {/* Personal Details */}
              <Grid item xs={12}>
                <CustomTextField
                  label={t("Full Name")}
                  value={profileData.full_name || ""}
                  name="full_name"
                  onChange={handleChange}
                  error={!!errors.full_name}
                  helperText={errors.full_name}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomTextField
                  label={t("Phone")}
                  name="phone_number"
                  value={profileData.phone_number || ""}
                  disabled
                  fullWidth
                  error={!!errors.phone_number}
                  helperText={errors.phone_number}
                  type="tel"
                />
              </Grid>

              <Grid item xs={12}>
                <CustomTextField
                  label={t("Alternate Phone")}
                  name="alternate_phone_number"
                  value={profileData.alternate_phone_number || ""}
                  onChange={handleChange}
                  error={!!errors.alternate_phone_number}
                  helperText={errors.alternate_phone_number}
                  type="tel"
                />
              </Grid>

              {/* Address Details */}
              <AddressFields
                profileData={profileData}
                handleChange={handleChange}
                postOffices={postOffices}
                pincodeError={pincodeError}
                fetchAddressDetails={fetchAddressDetails}
                t={t}
                errors={errors}
              />

              {/* Profile Picture */}
              <Grid item xs={12}>
                <Paper
                  elevation={3}
                  sx={{
                    width: "100%",
                    height: 200,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                    position: "relative",
                  }}
                >
                  {previews.profile_picture ? (
                    <img
                      src={previews.profile_picture || placeholder}
                      alt="Profile Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                  ) : (
                    <Person sx={{ fontSize: 80, color: "#ccc" }} />
                  )}
                </Paper>
                <input
                  id="profile-picture"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => handleImageUpload(e, "profile_picture")}
                />
                <Button
                  variant="outlined"
                  component="label"
                  htmlFor="profile-picture"
                  fullWidth
                  sx={{ color: "#4CAF50" }}
                >
                  <InsertPhoto
                    fontSize="medium"
                    sx={{ mr: 2, color: "#4CAF50" }}
                  />
                  {t("Upload Profile Picture")}
                </Button>
              </Grid>

              {/* KYC Documents */}
              <KYCFields
                profileData={profileData}
                handleChange={handleChange}
                handleImageUpload={handleImageUpload}
                previews={previews}
                t={t}
                errors={errors}
              />

              <Grid item xs={4}>
                <Button
                  type="submit"
                  variant="contained"
                  style={{ color: "white", width: "110px" }}
                >
                  {t("Save")}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Container>
      </LoaderContainer>
    </>
  );
}

export default PersonalInformation;
